import {useContext, useState} from 'react'

import PropTypes from 'prop-types'

import AtomButton, {
  atomButtonColors,
  atomButtonDesigns,
  atomButtonShapes,
  atomButtonSizes
} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import MoleculeAvatar from '@s-ui/react-molecule-avatar'
import MoleculeTextareaField from '@s-ui/react-molecule-textarea-field'
import IconComments from '@adv-mt/mt-svg-icons/lib/Comments'

import {BASE_CLASS, FORM_COMMENT_SPACING} from './config.js'
import {getClassNames} from './utils.js'
import {isEmptyComment, isValidComment} from './validateComment.js'

const TEXT_MAX_LENGTH = 2000
const EMPTY_TEXT = ''

export default function FormComment({inputPlaceholder, publish, cancel, spacing, user, shadow, showTitle}) {
  const [currentText, setCurrentText] = useState(EMPTY_TEXT)
  const [errorText, setErrorText] = useState(EMPTY_TEXT)
  const [isLoading, setIsLoading] = useState(false)

  const {rootClassNames, headingClassNames} = getClassNames({
    shadow,
    showTitle,
    spacing
  })

  const inputId = `commentText${user.id}`
  const {domain, i18n} = useContext(Context)
  const domainConfig = domain.get('config')
  const postErrorText = i18n.t('COMMENTS.PUBLISH_ERROR')
  const invalidCommentErrorText = i18n.t('COMMENTS.PUBLISH_INVALID_TEXT_ERROR')
  const textCharacters = i18n.t('COMMON.PLURALS.CHARACTER', {smart_count: TEXT_MAX_LENGTH})

  const onPublishClick = async () => {
    const {
      data: {articleId, parentCommentId}
    } = publish
    const {onAfterPublish} = publish

    if (isValidComment(currentText, domainConfig)) {
      setIsLoading(true)
      const [domainError, domainResponse] = await domain.get('post_news_comment_reviews_use_case').execute({
        articleId,
        comment: currentText,
        commentId: parentCommentId
      })

      if (domainError) setErrorText(postErrorText)

      if (domainResponse) {
        const callbackParams = {
          newCommentId: domainResponse.id,
          newCommentText: currentText,
          parentCommentId: parentCommentId || null
        }
        onAfterPublish(callbackParams)
        setErrorText(EMPTY_TEXT)
        setCurrentText('')
      }

      setIsLoading(false)
    }
  }

  const onInputChange = inputEvent => {
    const updatedText = inputEvent.target.value

    if (isValidComment(updatedText, domainConfig)) {
      setErrorText(EMPTY_TEXT)
    } else {
      setErrorText(invalidCommentErrorText)
    }

    setCurrentText(updatedText)
  }

  const isPublishButtonDisabled = isEmptyComment(currentText) || Boolean(errorText && errorText !== postErrorText)

  return (
    <div className={rootClassNames}>
      <div className={headingClassNames}>
        <MoleculeAvatar name={user.name} src={user.photoUrl} />
        {showTitle && <>{i18n.t('COMMENTS.PUBLISH_TITLE', {name: user.name})}</>}
      </div>
      <div className={`${BASE_CLASS}-inputAndActions`}>
        <MoleculeTextareaField
          id={inputId}
          maxChars={TEXT_MAX_LENGTH}
          name={inputId}
          placeholder={inputPlaceholder}
          resize="vertical"
          errorText={errorText}
          value={currentText}
          onChange={onInputChange}
          textCharacters={textCharacters}
        />
        <div className={`${BASE_CLASS}-actions`}>
          <AtomButton
            size={atomButtonSizes.SMALL}
            design={publish.button.design}
            shape={atomButtonShapes.CIRCULAR}
            leftIcon={publish.button.showIcon ? <IconComments /> : null}
            onClick={onPublishClick}
            disabled={isPublishButtonDisabled}
            data-testid={'publish-button'}
            isLoading={isLoading}
          >
            {i18n.t('COMMENTS.PUBLISH')}
          </AtomButton>
          {cancel && (
            <AtomButton
              size={atomButtonSizes.SMALL}
              color={atomButtonColors.PRIMARY}
              design={atomButtonDesigns.OUTLINE}
              shape={atomButtonShapes.CIRCULAR}
              onClick={cancel.button.onClick}
            >
              {i18n.t('COMMENTS.CANCEL')}
            </AtomButton>
          )}
        </div>
      </div>
    </div>
  )
}

FormComment.displayName = 'FormComment'
FormComment.propTypes = {
  inputPlaceholder: PropTypes.string,
  shadow: PropTypes.bool,
  showTitle: PropTypes.bool,
  spacing: PropTypes.oneOf(Object.values(FORM_COMMENT_SPACING)),
  user: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    photoUrl: PropTypes.string
  }),
  publish: PropTypes.shape({
    button: PropTypes.shape({
      color: PropTypes.oneOf(Object.values(atomButtonColors)),
      design: PropTypes.oneOf(Object.values(atomButtonDesigns)),
      showIcon: PropTypes.bool
    }),
    data: {
      articleId: PropTypes.string.isRequired,
      parentCommentId: PropTypes.string
    },
    onAfterPublish: PropTypes.func
  }),
  cancel: PropTypes.shape({
    button: PropTypes.shape({
      onClick: PropTypes.func
    })
  })
}

export * from './config.js'
